// The entry point: mount, connect, and drive the two clocks.
//
// There are two, they run at different rates, and keeping them apart is the whole
// job of this file:
//
//	THE NETWORK CLOCK is the server's 500 ms tick. Frames arrive, the follower
//	  judges continuity, the board replica marks dirty regions, and the arc field
//	  takes a new set of flows. None of that touches the display.
//
//	THE DISPLAY CLOCK is requestAnimationFrame. It uploads whatever is dirty and
//	  draws — every frame, whether or not a tick arrived. An arc whose animation
//	  advanced only when a frame landed would stutter at 2 Hz, and §15's tracer is
//	  a 600 ms animation over a 500 ms tick.
//
// And one thing that is neither: the HEARTBEAT. The server holds a 120 s read
// deadline and closes an idle connection with RejectIdle. An empty SUBSCRIBE
// carrying the last applied tick is the whole message — 13 bytes — and it is also
// the only staleness signal the server ever gets. Connection owns the timer;
// this file sets its period and gives it an extra beat when a backgrounded tab
// comes back, because a throttled timer in a hidden tab is exactly the case the
// read deadline is waiting for.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App, type RendererSlot } from './App';
import { Connection } from './net/connection';
import { ClickBook } from './net/clicks';
import { defaultSocketURL, sessionToken } from './net/session';
import { Palette } from './render/palette';
import { ArcField } from './render/arcs';
import { AudioEngine } from './audio/engine';
import { announcerLineFor } from './audio/announcer';
import { HudStore, type FeedEntry } from './ui/hudState';
import { installGlobalCSS } from './ui/css';
import { prefersReducedMotion, resolveReducedMotion } from './ui/motion';
import { FlowFortressSiege, FlowKill } from './wire/ephemeral';
import { isFailure, OutcomeCaptured } from './net/clicks';

/**
 * Render hints, not game rules.
 *
 * The same two numbers MapRenderer defaults to. They are here because the siege
 * heat the arc layer wants — "a fortress at 40% HP should be legible as a thing
 * under attack from across the map" — is computed from the board's HP against the
 * fortress ceiling, and a second, different guess at that ceiling would make the
 * arcs and the pixel layer disagree about how hurt a fortress is.
 */
const BaseHP = 10;
const FortressHP = BaseHP * 50;

/** How many impacts a single tick may play. */
const MaxImpactsPerTick = 3;

function boot(): void {
  const root = document.getElementById('root');
  if (!root) throw new Error('main: #root is missing from index.html');

  installGlobalCSS(document);

  const storage = safeStorage();
  const store = new HudStore();
  const palette = new Palette();
  const audio = new AudioEngine({ store: storage });

  // A single clock for everything the renderer compares against. ClickBook
  // defaults to Date.now() and MapRenderer.draw defaults to performance.now();
  // left alone, an optimistic marker's age would be computed against one and its
  // animation against the other, and the overlay would show nothing at all.
  const now = (): number => performance.now();

  const arcs = new ArcField({
    siegeHeat: (target) => {
      const hp = connection.board.hpAt(target);
      return 1 - Math.min(1, hp / FortressHP);
    },
  });

  const connection = new Connection({
    url: defaultSocketURL(),
    token: sessionToken(),
    now,
    clickBook: new ClickBook({ now }),
    // A quarter of the server's 120 s read deadline, so two lost beats are still
    // not a disconnect.
    heartbeatMs: 30_000,
    events: {
      onStateChange: (s) => store.setLink(s),
      onWelcome: (w) => store.setWelcome(w.country, w.locked),
      onFrame: (tick) => {
        store.setTick(tick);
        store.setBank(connection.clicks.bankState(), connection.clicks.bankKnown());
      },
      onGap: () => {
        // §3: the board is discarded and a keyframe is awaited. The fire drawn
        // over it goes too — arcs describe a board this client no longer has, and
        // leaving them up would show a front that has already moved.
        arcs.clear();
        store.setGap();
      },
      onSelf: (s, resolved) => {
        store.setBank(connection.clicks.bankState(), connection.clicks.bankKnown());
        if (!resolved) return;
        store.setOutcome(resolved.outcome, s.verdict, Date.now());
        if (resolved.outcome === OutcomeCaptured) audio.ui('capture', { weight: 0.8 });
        else if (resolved.outcome === 'lost') audio.ui('lost', { weight: 0.5 });
        else if (isFailure(resolved.outcome)) audio.ui('refuse', { weight: 0.4 });
      },
      onEphemeral: (e) => {
        arcs.ingest(e, palette, now());

        // §16: "Every line is also a text event in the feed. Audio is never the
        // only channel for anything." The feed is written first, and the
        // announcer is asked second — so a build with no voice bank, which is
        // every build today, loses nothing.
        if (e.lines.length > 0) {
          const entries: Omit<FeedEntry, 'id' | 'at'>[] = e.lines.map((l) => ({
            kind: l.kind,
            country: l.country,
            other: l.other,
            pixel: l.pixel,
            hasPixel: l.hasPixel,
            text: l.text,
          }));
          store.pushFeed(entries, Date.now());
          for (const l of e.lines) {
            const line = announcerLineFor(l.kind);
            if (line) audio.announce({ line, country: l.country });
          }
        }

        // Impacts, for the heaviest few flows only. Two hundred and fifty tracers
        // land in the same 500 ms window on a busy front and two hundred and
        // fifty impact sounds is white noise, not a battle.
        const f = e.pixelFlows;
        if (f && f.count > 0) {
          const order: number[] = [];
          for (let i = 0; i < f.count; i++) order.push(i);
          order.sort((a, b) => (f.weight[b] ?? 0) - (f.weight[a] ?? 0) || a - b);
          for (const i of order.slice(0, MaxImpactsPerTick)) {
            const flags = f.flags[i] ?? 0;
            audio.impact(
              (f.weight[i] ?? 0) / 255,
              (flags & FlowKill) !== 0,
              (flags & FlowFortressSiege) !== 0,
            );
          }
        }
      },
      onReject: (r) => store.setReject(r.code, r.reason),
      onError: (e) => {
        // Kept to the console on purpose: a decode failure is a developer event,
        // and the player-facing channel for a connection problem is the LINK
        // panel, which the state change has already updated.
        console.error('wwp:', e);
      },
    },
  });

  const rendererSlot: RendererSlot = { current: null };
  const reduced = resolveReducedMotion(storage, undefined);
  store.setReducedMotion(reduced);
  store.setAudio(audio.live(), audio.volumes());
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.motion = reduced ? 'reduced' : 'full';
  }

  createRoot(root).render(
    <StrictMode>
      <App
        connection={connection}
        store={store}
        audio={audio}
        arcs={arcs}
        palette={palette}
        rendererSlot={rendererSlot}
        motionStore={storage}
        initialReducedMotion={reduced}
      />
    </StrictMode>,
  );

  void connection.connect();

  // --- the display clock --------------------------------------------------

  let raf = 0;
  const frame = (): void => {
    raf = requestAnimationFrame(frame);
    const renderer = rendererSlot.current;
    if (!renderer) return;
    const t = now();
    // Order is load-bearing. Expiring clicks and pruning arcs before the draw
    // means a marker or a tracer that has outlived its window is gone this frame
    // rather than the next one, which is the difference between an animation that
    // ends and one that flickers on its last frame.
    connection.clicks.expire(t);
    arcs.prune(t);
    renderer.upload(connection.board);
    renderer.draw(connection.board, connection.clicks.active(), t);
    store.setArcs(arcs.count());
  };
  raf = requestAnimationFrame(frame);

  // --- the heartbeat's safety net -----------------------------------------

  document.addEventListener('visibilitychange', () => {
    // A hidden tab has its timers throttled to roughly once a minute, which is
    // still inside the 120 s deadline — but the transition back is the moment the
    // player is about to act, and a beat here costs 13 bytes and removes the
    // whole class of "came back to a dead socket".
    if (document.visibilityState === 'visible') connection.sendHeartbeat();
  });

  window.addEventListener('pagehide', () => {
    cancelAnimationFrame(raf);
    connection.close(1000, 'navigating away');
  });
}

/**
 * localStorage, or nothing.
 *
 * Safari in a private window throws on ACCESS, not on write, so the probe has to
 * be inside the try. A player whose storage is unavailable gets defaults and a
 * session that forgets — which is the correct degradation, and §4 already says
 * the durable identity is the cookie rather than script-writable storage.
 */
function safeStorage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    const probe = '__wwp__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

// Seeded before the first paint so the very first frame already honours the
// player's stated preference. §15: this is not negotiable and it is not a
// setting they should have to find.
if (prefersReducedMotion() && typeof document !== 'undefined') {
  document.documentElement.dataset.motion = 'reduced';
}

boot();
